import React from 'react';
import Helmet from 'react-helmet';

interface PageMetaProps {
  title: string;
  description?: string;
  index?: boolean;
  follow?: boolean;
  enableHreflang?: boolean;
  route: string;
  hostname: string;
  siteLanguage: string;
  classification: {
    brand: string;
    product: string;
  };
}

const capitaliseFirstLetter = (str: string) =>
  `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

export const PageMeta: React.FC<PageMetaProps> = ({
  title,
  description,
  index,
  follow,
  enableHreflang,
  route,
  hostname,
  siteLanguage,
  classification,
}) => {
  const meta = React.useMemo(() => {
    const generalMeta = [{ name: 'description', content: description }];

    const robotsValues: Record<string, boolean> = {
      index: Boolean(index),
      follow: Boolean(follow),
    };

    const robotMeta = Object.entries(robotsValues).reduce<string[]>(
      (carry, [key, value]) => (value ? [...carry, key] : carry),
      [],
    );

    if (robotMeta.length) {
      generalMeta.push({ name: 'robots', content: robotMeta.join(',') });
    }

    return [...generalMeta].filter((metaData) => Boolean(metaData.content));
  }, [description, follow, index]);

  let routeValueFiltered = route.replace('/id/', '').replace('/th/', '');
  if (routeValueFiltered.startsWith('/')) {
    routeValueFiltered = routeValueFiltered.substring(1);
  }

  const hreflangs = [
    {
      hreflang: 'en-my',
      href: `https://my.employer.seek.com/${routeValueFiltered}`,
    },
    {
      hreflang: 'en-sg',
      href: `https://sg.employer.seek.com/${routeValueFiltered}`,
    },
    {
      hreflang: 'en-ph',
      href: `https://ph.employer.seek.com/${routeValueFiltered}`,
    },
    {
      hreflang: 'en-id',
      href: `https://id.employer.seek.com/${routeValueFiltered}`,
    },
    {
      hreflang: 'id-id',
      href: `https://id.employer.seek.com/id/${routeValueFiltered}`,
    },
    {
      hreflang: 'en-hk',
      href: `https://hk.employer.seek.com/${routeValueFiltered}`,
    },
    {
      hreflang: 'en-th',
      href: `https://th.employer.seek.com/${routeValueFiltered}`,
    },
    {
      hreflang: 'th-th',
      href: `https://th.employer.seek.com/th/${routeValueFiltered}`,
    },
    {
      hreflang: 'en-au',
      href: `https://talent.seek.com.au/${routeValueFiltered}`,
    },
    {
      hreflang: 'en-nz',
      href: `https://talent.seek.co.nz/${routeValueFiltered}`,
    },
  ];

  let canonUrl;
  switch (siteLanguage) {
    case 'en':
      canonUrl = `https://${hostname}/${routeValueFiltered}`;
      break;
    default:
      canonUrl = `https://${hostname}/${siteLanguage}/${routeValueFiltered}`;
      break;
  }

  return (
    <Helmet
      meta={meta}
      title={`${title} | ${classification.brand.toUpperCase()} ${capitaliseFirstLetter(
        classification.product,
      )}`}
    >
      {enableHreflang &&
        hreflangs.map((e, num) => <link rel="alternate" key={num} {...e} />)}
      {(routeValueFiltered === 'contactus' ||
        routeValueFiltered === 'employer-seek-id') && (
        <link rel="canonical" href={canonUrl} />
      )}
      {routeValueFiltered === 'clear-browser-history' && (
        <meta name="robots" content="noindex" />
      )}
    </Helmet>
  );
};
